"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

// Intent delay: first tooltip opens after 200ms; moving to a neighbour within 300ms opens it instantly
function TooltipProvider({ delayDuration = 200, skipDelayDuration = 300, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration} {...props} />
}

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Content>, React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & { variant?: "dark" | "light" }>(({ className, sideOffset = 6, variant = "dark", ...props }, ref) => (
    <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                "z-50 overflow-hidden rounded-lg px-3 py-2 text-[13px] leading-relaxed shadow-lg",
                variant === "dark" && "bg-gray-900 text-white [&_*]:text-white",
                variant === "light" && "bg-white text-gray-900 border border-gray-200/80 shadow-xl shadow-black/10",
                "animate-in fade-in-0 zoom-in-95",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
                "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                // Hand-off between neighbouring tooltips is instant: no enter animation
                "data-[state=instant-open]:animate-none",
                className
            )}
            {...props}
        />
    </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * TouchTooltip - Hybrid tooltip that works on both desktop (hover) and mobile (tap).
 *
 * Radix Tooltip is hover-only by design. This component uses onPointerDown with
 * pointerType detection for instant, reliable mobile tap support.
 * Desktop hover behavior is preserved exactly as-is via Radix's onOpenChange.
 */
interface TouchTooltipProps {
    children: React.ReactNode
    content: React.ReactNode
    side?: "top" | "bottom" | "left" | "right"
    variant?: "dark" | "light"
    className?: string
    contentClassName?: string
    delayDuration?: number
    asChild?: boolean
}

function TouchTooltip({ children, content, side = "top", variant = "light", className, contentClassName, delayDuration = 200, asChild = true }: TouchTooltipProps) {
    const [open, setOpen] = React.useState(false)
    const triggerRef = React.useRef<HTMLElement>(null)
    // Track whether the current interaction is touch-based
    const isTouchInteraction = React.useRef(false)

    // Close on outside tap/click when open
    React.useEffect(() => {
        if (!open) return
        const handleOutside = (e: PointerEvent) => {
            if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        // Delay listener registration so the opening tap doesn't immediately close
        const raf = requestAnimationFrame(() => {
            document.addEventListener("pointerdown", handleOutside, { passive: true })
        })
        return () => {
            cancelAnimationFrame(raf)
            document.removeEventListener("pointerdown", handleOutside)
        }
    }, [open])

    // Use onPointerDown to detect touch vs mouse directly from the event
    const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
        if (e.pointerType === "touch") {
            e.preventDefault()
            isTouchInteraction.current = true
            setOpen(prev => !prev)
        }
    }, [])

    return (
        <TooltipProvider delayDuration={delayDuration}>
            <TooltipPrimitive.Root
                open={open}
                onOpenChange={(newOpen) => {
                    // Only let Radix control open state for mouse (hover) interactions
                    // Touch interactions are controlled exclusively by handlePointerDown
                    if (!isTouchInteraction.current) {
                        setOpen(newOpen)
                    }
                    // Reset touch flag when Radix fires close (pointer left)
                    if (!newOpen) isTouchInteraction.current = false
                }}
            >
                <TooltipPrimitive.Trigger
                    asChild={asChild}
                    ref={triggerRef as any}
                    onPointerDown={handlePointerDown}
                >
                    {children}
                </TooltipPrimitive.Trigger>
                <TooltipContent side={side} variant={variant} className={cn(contentClassName)}>
                    {content}
                </TooltipContent>
            </TooltipPrimitive.Root>
        </TooltipProvider>
    )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TouchTooltip }
